import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, Link } from 'react-router-dom'
import { CreditCard, ArrowLeft, Trash2, Plus, Minus, Loader2, AlertCircle, MapPin, Pencil, Check } from 'lucide-react'
import { useCheckoutBundleStore } from '../store/checkoutBundleStore'
import { createOrder } from '../api/orders'
import { getProfile, updateProfile } from '../api/library'
import { trackEvent, buildBundleParams } from '../utils/metaPixel'
import UiButton from '../components/ui/UiButton'
import ShippingAddressForm, { normalizeAddress, isAddressComplete, formatAddress } from '../components/ShippingAddressForm'
import PhoneInput, { isValidPhone } from '../components/PhoneInput'

export default function CheckoutPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { items, updateQuantity, removeTier, clearBundle, getTotalMonthlyPrice, getTotalItemCount } = useCheckoutBundleStore()
  const totalMonthlyPrice = getTotalMonthlyPrice()
  const totalItemCount = getTotalItemCount()
  const hasItems = items.length > 0

  const [paying, setPaying] = useState(false)
  const [orderError, setOrderError] = useState(null)

  // Profile / address state
  const [profile, setProfile] = useState(null)
  const [profileLoading, setProfileLoading] = useState(true)
  const [profileError, setProfileError] = useState(null)
  const [address, setAddress] = useState(normalizeAddress())
  const [phone, setPhone] = useState('')
  const [editingAddress, setEditingAddress] = useState(false)
  const [savingAddress, setSavingAddress] = useState(false)
  const [addressSaved, setAddressSaved] = useState(false)

  const isPhoneRequired = address.type === 'paczkomat'
  const isPhoneValid = !isPhoneRequired || isValidPhone(phone)

  useEffect(() => {
    let cancelled = false
    setProfileLoading(true)
    getProfile()
      .then((data) => {
        if (cancelled) return
        setProfile(data)
        const existing = normalizeAddress(data?.shippingAddress)
        setAddress(existing)
        setPhone(data?.phoneNumber || '')
        const needsPhone = existing.type === 'paczkomat' && !isValidPhone(data?.phoneNumber || '')
        setEditingAddress(!isAddressComplete(existing) || needsPhone)
      })
      .catch((err) => {
        if (cancelled) return
        if (err.status === 401) {
          navigate(`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`)
          return
        }
        setProfileError(err.message)
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [navigate])

  useEffect(() => {
    if (items.length > 0) {
      trackEvent('ViewCart', buildBundleParams(items))
    }
  }, [items])

  const handleSaveAddress = async () => {
    if (!isAddressComplete(address)) return
    if (isPhoneRequired && !isValidPhone(phone)) return
    setSavingAddress(true)
    setAddressSaved(false)
    try {
      const update = { shippingAddress: address }
      if (phone) update.phoneNumber = phone
      const updated = await updateProfile(update)
      setProfile(updated)
      setAddress(normalizeAddress(updated.shippingAddress))
      setPhone(updated.phoneNumber || phone)
      setEditingAddress(false)
      setAddressSaved(true)
      setTimeout(() => setAddressSaved(false), 4000)
    } catch (err) {
      setOrderError(err.message)
    } finally {
      setSavingAddress(false)
    }
  }

  const handlePay = async () => {
    if (editingAddress) {
      setOrderError(t('checkout.saveAddressRequired', 'Zapisz adres dostawy, aby przejść do płatności.'))
      return
    }

    if (!isAddressComplete(address)) {
      setOrderError(t('checkout.addressRequired', 'Dodaj adres dostawy, aby przejść do płatności.'))
      setEditingAddress(true)
      return
    }

    if (isPhoneRequired && !isValidPhone(phone)) {
      setOrderError(t('checkout.address.phoneRequired', 'Numer telefonu jest wymagany przy dostawie do Paczkomatu.'))
      setEditingAddress(true)
      return
    }

    if (!hasItems) {
      setOrderError(t('checkout.bundleEmpty', 'Nie wybrałeś jeszcze żadnego abonamentu.'))
      return
    }

    setPaying(true)
    setOrderError(null)

    try {
      const subscriptionIds = []
      const preferences = {}

      items.forEach((item) => {
        for (let i = 0; i < item.quantity; i++) {
          subscriptionIds.push(item.slug)
        }
        if (item.preferences) {
          preferences[item.slug] = item.preferences
        }
      })

      trackEvent('InitiateCheckout', buildBundleParams(items))

      const result = await createOrder({ type: 'recurring', subscriptionIds, preferences })
      clearBundle()

      if (result?.redirectUrl) {
        window.location.href = result.redirectUrl
        return
      }

      setOrderError(t('checkout.missingRedirectUrl', 'Brak adresu przekierowania do płatności.'))
    } catch (err) {
      setOrderError(err.message)
    } finally {
      setPaying(false)
    }
  }

  if (profileLoading) {
    return (
      <div className="max-w-3xl mx-auto pb-12 text-center">
        <Loader2 className="mx-auto animate-spin text-brand-blue" size={48} />
        <p className="text-brand-blue mt-4">{t('common.loading')}</p>
      </div>
    )
  }

  if (profileError) {
    return (
      <div className="max-w-3xl mx-auto pb-12 text-center">
        <p className="text-red-600">{profileError}</p>
      </div>
    )
  }

  const addressComplete = isAddressComplete(address)
  const deliveryReady = addressComplete && isPhoneValid

  return (
    <div className="max-w-3xl mx-auto pb-12">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="text-brand-orange hover:underline inline-flex items-center gap-1 mb-4"
      >
        <ArrowLeft size={18} /> {t('common.goHome')}
      </button>

      <h1 className="font-display text-4xl md:text-5xl text-brand-blue mb-6">
        {t('checkout.title')}
      </h1>

      <div className="bg-brand-cream/95 rounded-3xl p-6 md:p-10 shadow-xl space-y-6">
        <h2 className="font-display text-2xl text-brand-blue">
          {t('checkout.bundleTitle', 'Twoja subskrypcja')}
        </h2>

        {!hasItems ? (
          <div className="text-center py-8">
            <p className="text-gray-700 mb-4">{t('checkout.bundleEmpty')}</p>
            <Link
              to="/"
              className="inline-block bg-brand-orange text-white rounded-full px-6 py-2 font-bold hover:bg-brand-gold transition"
            >
              {t('checkout.bundleBrowse')}
            </Link>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {items.map((item) => (
                <div key={item.slug} className="flex items-center gap-4 bg-white p-4 rounded-xl">
                  <img
                    src={item.image}
                    alt=""
                    className="w-16 h-24 object-cover rounded shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-brand-blue">{item.name}</h3>
                    <p className="text-sm text-gray-600">
                      {item.priceMonthly} zł / {t('subscription.month')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.slug, -1)}
                      className="p-2 rounded-full bg-brand-cream text-brand-blue hover:bg-brand-gold/30 transition"
                      aria-label={t('common.remove', 'Usuń jeden')}
                    >
                      <Minus size={16} />
                    </button>
                    <span className="w-8 text-center font-bold text-brand-blue">{item.quantity}</span>
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.slug, 1)}
                      className="p-2 rounded-full bg-brand-cream text-brand-blue hover:bg-brand-gold/30 transition"
                      aria-label={t('common.add', 'Dodaj jeden')}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeTier(item.slug)}
                    className="p-2 text-red-500 hover:text-red-700 transition"
                    aria-label={t('checkout.remove', 'Usuń')}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>

            <div className="bg-brand-blue/5 border border-brand-blue/10 rounded-xl p-4">
              <p className="text-sm text-brand-blue/90">
                {t('checkout.bundleWarning')}{' '}
                <a
                  href={`mailto:${t('checkout.bundleWarningEmail')}`}
                  className="underline hover:text-brand-orange"
                >
                  {t('checkout.bundleWarningEmail')}
                </a>
              </p>
            </div>

            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
              <span className="text-xl font-bold text-brand-blue">{t('cart.total')}</span>
              <span className="text-2xl font-bold text-brand-orange">{totalMonthlyPrice} zł</span>
            </div>
            <p className="text-sm text-gray-600 text-right">
              {t('subscription.shippingNote', { price: 15, defaultValue: '+15 zł wysyłka' })}
            </p>
          </>
        )}
      </div>

      {/* Shipping address gate */}
      <div className="mt-6 bg-brand-cream/95 rounded-3xl p-6 md:p-10 shadow-xl space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <MapPin className="text-brand-orange" size={28} />
            <h2 className="font-display text-2xl text-brand-blue">
              {t('checkout.address.title', 'Adres dostawy')}
            </h2>
          </div>
          {!editingAddress && (
            <button
              type="button"
              onClick={() => setEditingAddress(true)}
              className="flex items-center gap-2 text-brand-blue hover:text-brand-orange transition font-bold text-sm"
            >
              <Pencil size={16} />
              {t('library.profile.edit', 'Edytuj')}
            </button>
          )}
        </div>

        {addressSaved && (
          <div className="flex items-center gap-2 bg-green-100 text-green-800 rounded-xl px-4 py-3">
            <Check size={20} />
            <span className="font-bold">{t('library.profile.saved', 'Zapisano zmiany.')}</span>
          </div>
        )}

        {editingAddress ? (
          <>
            {!addressComplete && profile?.shippingAddress && (
              <p className="text-base text-brand-orange/90 bg-brand-orange/10 rounded-xl p-4">
                {t('checkout.address.incomplete', 'Twój zapisany adres jest niepełny. Uzupełnij brakujące pola, aby zapisać i przejść do płatności.')}
              </p>
            )}
            <ShippingAddressForm
              value={address}
              onChange={setAddress}
              disabled={savingAddress}
            />

            {isPhoneRequired && (
              <PhoneInput
                label={t('checkout.address.phone', 'Numer telefonu')}
                value={phone}
                onChange={setPhone}
                placeholder={t('checkout.address.phonePlaceholder', 'np. 123 456 789')}
                disabled={savingAddress}
                required
                showValidation={true}
              />
            )}

            <div className="flex flex-wrap gap-3">
              <UiButton
                type="button"
                variant="gold"
                disabled={!deliveryReady || savingAddress}
                onClick={handleSaveAddress}
                icon={savingAddress ? Loader2 : Check}
              >
                {savingAddress ? t('common.saving', 'Zapisywanie...') : t('checkout.address.save', 'Zapisz adres dostawy')}
              </UiButton>
              {deliveryReady && (
                <UiButton
                  type="button"
                  variant="secondary"
                  disabled={savingAddress}
                  onClick={() => setEditingAddress(false)}
                >
                  {t('common.cancel', 'Anuluj')}
                </UiButton>
              )}
            </div>
          </>
        ) : deliveryReady ? (
          <div className="bg-white rounded-xl p-4 space-y-1">
            <p className="text-sm font-semibold text-brand-blue mb-1">
              {address.type === 'paczkomat'
                ? t('checkout.address.paczkomat', 'Paczkomat')
                : t('checkout.address.home', 'Adres domowy')}
            </p>
            <p className="text-base text-brand-blue/80 font-body">{formatAddress(address)}</p>
            {address.type === 'paczkomat' && phone && (
              <p className="text-base text-brand-blue/80 font-body">{phone}</p>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl p-4">
            <p className="text-base text-brand-blue/80">
              {t('checkout.address.missing', 'Nie podano jeszcze adresu dostawy. Dodaj go, aby przejść do płatności.')}
            </p>
            <UiButton
              type="button"
              variant="gold"
              className="mt-4"
              onClick={() => setEditingAddress(true)}
            >
              {t('checkout.address.add', 'Dodaj adres dostawy')}
            </UiButton>
          </div>
        )}
      </div>

      <div className="mt-6 bg-brand-cream/95 rounded-3xl p-6 md:p-10 shadow-xl space-y-6">
        {orderError && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-800">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 shrink-0" size={20} />
              <div className="flex-1 space-y-2">
                <p>{orderError}</p>
                <Link
                  to="/moja-biblioteka/subskrypcje"
                  className="inline-block text-brand-blue hover:underline font-semibold"
                >
                  {t('library.mySubscriptions.checkoutError.goToSubscriptions', 'Przejdź do subskrypcji')}
                </Link>
              </div>
            </div>
          </div>
        )}

        <UiButton
          type="button"
          size="lg"
          fullWidth
          disabled={!hasItems || paying || !deliveryReady || editingAddress}
          onClick={handlePay}
          icon={paying ? Loader2 : CreditCard}
          className={!deliveryReady || editingAddress ? 'opacity-60' : ''}
        >
          {paying
            ? t('checkout.redirecting')
            : editingAddress
              ? t('checkout.saveAddressRequired', 'Zapisz adres dostawy, aby przejść do płatności')
              : !addressComplete
                ? t('checkout.addressRequired', 'Wymagany adres dostawy')
                : isPhoneRequired && !isValidPhone(phone)
                  ? t('checkout.address.phoneRequired', 'Wymagany numer telefonu do Paczkomatu')
                  : t('checkout.continueToPayment', 'Przejdź do płatności')}
        </UiButton>

        <p className="text-sm text-gray-600 text-center">
          {t('checkout.redirectMessage')}
        </p>
      </div>
    </div>
  )
}
